import { PureComponent, ReactNode } from 'react'
import { DispatchProp } from 'react-redux'
import { RouteComponentProps, withRouter } from '../../../helpers/withRouter'
import Select from 'react-select'
import { Button, Page, Title, ValidatedInput } from '../../../components'
import CountryPickerModal from '../../../containers/CountryPickerModal'
import { connectRedux } from '../../../helpers/connectRedux'
import countries from '../../../countries.json'
import { isDniValid, isTelephoneValid, isTextValid } from '../../../helpers'
import { appsLogosSelected, appsRegisterSelected, appsStyleSelected } from '../../../helpers/app'
import { images } from '../../../images'
import { IGenderType } from '../../../models'
import { ICountry } from '../../../models/country'
import { urls } from '../../../routing'
import { LiteralsService } from '../../../services'
import {
  setSignUpAdditionalPhoneAction,
  setSignUpAdditionalPhoneCallingCodeAction,
  setSignUpAdditionalPhoneCountryAction,
  setSignUpBirthDayAction,
  setSignUpBirthMonthAction,
  setSignUpBirthYearAction,
  setSignUpDNIAction,
  setSignUpGenderAction,
  setSignUpIsPrmAction,
  setSignUpLastNameAction,
  setSignUpNameAction,
  setSignUpSettlementAction
} from '../../../store/actions'
import { IStoreState } from '../../../store/states'
import StyledArticle from '../styles'
import { AnyAction } from 'redux'

const registerData = appsRegisterSelected?.data || []

interface IProps extends DispatchProp<AnyAction>, RouteComponentProps {
  name: string
  lastName: string
  dni: string
  email: string
  birthDay: string
  birthMonth: string
  birthYear: string
  gender: string
  isPrm: boolean
  settlement: string
  additionalPhone: string
  additionalPhoneCountry?: ICountry
  additionalPhoneCallingCode: string
}

class SignUpStep2 extends PureComponent<IProps> {
  constructor(props: IProps) {
    super(props)
    if (registerData.includes('additionalPhone') && !props.additionalPhoneCountry) {
      const defaultCountry = countries.find(country => country.code === 'ES')
      if (defaultCountry) {
        props.dispatch(setSignUpAdditionalPhoneCountryAction(defaultCountry))
        props.dispatch(setSignUpAdditionalPhoneCallingCodeAction(defaultCountry.dial_code))
      }
    }
  }

  private get buttonIsDisabled(): boolean {
    const { name, lastName, dni, birthDay, birthMonth, birthYear, gender, settlement, additionalPhone } = this.props
    const showDNI = process.env.REACT_APP_SHOW_DNI === 'true'

    let disabled = !isTextValid(name) || !isTextValid(lastName)
    disabled = showDNI ? disabled || !(isDniValid(dni) || isDniValid(dni)) : disabled
    if (registerData.includes('birthdate')) disabled = disabled || !birthDay || !birthMonth || !birthYear
    if (registerData.includes('gender')) disabled = disabled || !gender
    if (registerData.includes('village')) disabled = disabled || !isTextValid(settlement)
    if (registerData.includes('additionalPhone') && additionalPhone) disabled = disabled || !isTelephoneValid(additionalPhone)

    return disabled
  }

  private get dniInvalidError(): ReactNode {
    const dni = this.props.dni
    if (!dni || dni.length > 9 || dni.length < 9) return null
    return (!!dni && !isDniValid(dni)) && (
      <div className="error-container">
        <span className="error">
          { LiteralsService.get('dniInvalid', true) }
        </span>
      </div>
    )
  }

  private get genderOptions() {
    return Object.values(IGenderType).map(value => ({
      value,
      label: LiteralsService.get(value.toLowerCase(), true)
    }))
  }

  private get birthdateFields(): ReactNode {
    const { birthDay, birthMonth, birthYear } = this.props
    return (
      <div className="birthdate-row">
        <ValidatedInput
          name="birthDay"
          label={ LiteralsService.get('day', true) }
          value={ birthDay }
          onChange={ this.onBirthDayChange }
          error={ !birthDay }
        />
        <ValidatedInput
          name="birthMonth"
          label={ LiteralsService.get('month', true) }
          value={ birthMonth }
          onChange={ this.onBirthMonthChange }
          error={ !birthMonth }
        />
        <ValidatedInput
          name="birthYear"
          label={ LiteralsService.get('year', true) }
          value={ birthYear }
          onChange={ this.onBirthYearChange }
          error={ !birthYear }
        />
      </div>
    )
  }

  public render(): ReactNode {

    const { name, lastName, dni, gender, settlement, isPrm, additionalPhone, additionalPhoneCountry } = this.props
    const showDNI = process.env.REACT_APP_SHOW_DNI === 'true'

    return (
      <Page className={ appsStyleSelected?.style }>
        <StyledArticle>
          <form onSubmit={ e => e.preventDefault() }>
            <img src={ images['goBack'] } className="backButton" onClick={ this.handleGoBack } alt="" />
            <header>
              <img  className={ appsLogosSelected?.classRounded }
                    src={ appsLogosSelected?.logoRounded }
                    alt={ appsLogosSelected?.alt } />
            </header>
            <Title text={ LiteralsService.get('whoAreYou', true) } />
            <section>
              <ValidatedInput
                name="name"
                label={ LiteralsService.get('name', true) }
                value={ name }
                onChange={ this.onNameChange }
                error={ !isTextValid(name) }
              />
              <ValidatedInput
                name="lastName"
                label={ LiteralsService.get('lastName', true) }
                value={ lastName }
                onChange={ this.onLastNameChange }
                error={ !isTextValid(lastName) }
              />
              { registerData.includes('birthdate') && this.birthdateFields }
              { registerData.includes('gender') &&
                <Select
                  className="select"
                  options={ this.genderOptions }
                  placeholder={ LiteralsService.get('gender', true) }
                  onChange={ this.onGenderChange }
                  value={ gender ? this.genderOptions.find(option => option.value === gender) : null }
                />
              }
              { registerData.includes('village') &&
                <ValidatedInput
                  name="settlement"
                  label={ LiteralsService.get('village', true) }
                  value={ settlement }
                  onChange={ this.onSettlementChange }
                  error={ !isTextValid(settlement) }
                />
              }
              { registerData.includes('isPrm') &&
                <div className="prm-container" onClick={ () => this.onIsPrmChange(!isPrm) }>
                  <img src={ images[ isPrm ? 'checkBoxFilled' : 'checkBoxEmpty' ] } alt="" />
                  <span>{ LiteralsService.get('reducedMobilityPerson', true) }</span>
                </div>
              }
              { registerData.includes('additionalPhone') &&
                <ValidatedInput
                  name="additionalPhone"
                  phone
                  label={ LiteralsService.get('additionalPhone', true) }
                  value={ additionalPhone }
                  country={ additionalPhoneCountry }
                  onChange={ this.onAdditionalPhoneChange }
                  error={ !!additionalPhone && !isTelephoneValid(additionalPhone) }
                />
              }
              { showDNI ?
                <ValidatedInput
                  name="dni"
                  label={ LiteralsService.get('dni').toUpperCase() }
                  value={ dni }
                  onChange={ this.onDNIChange }
                  error={ !isDniValid(dni) }
                  tooltip={ LiteralsService.get('dniHint', true) }
                />
              : '' }
              { showDNI ? this.dniInvalidError : '' }
            </section>
            { registerData.includes('additionalPhone') && <CountryPickerModal selectCountry={ this.onAdditionalPhoneCountryChange } /> }
            <section className="actions">
              <Button
                title={ LiteralsService.get('continue') }
                onPress={ this.goToStep3 }
                filled
                upper
                isDisabled={ this.buttonIsDisabled }
              />
            </section>
          </form>
        </StyledArticle>
      </Page>
    )
  }

  private onNameChange = (name: string) => {
    const { dispatch } = this.props
    dispatch(setSignUpNameAction(name))
  }

  private onLastNameChange = (lastName: string) => {
    const { dispatch } = this.props
    dispatch(setSignUpLastNameAction(lastName))
  }

  private onDNIChange = (dni: string) => {
    const { dispatch } = this.props
    dispatch(setSignUpDNIAction(dni))
  }

  private onBirthDayChange = (birthDay: string) => {
    this.props.dispatch(setSignUpBirthDayAction(birthDay))
  }

  private onBirthMonthChange = (birthMonth: string) => {
    this.props.dispatch(setSignUpBirthMonthAction(birthMonth))
  }

  private onBirthYearChange = (birthYear: string) => {
    this.props.dispatch(setSignUpBirthYearAction(birthYear))
  }

  private onGenderChange = (option: any) => {
    this.props.dispatch(setSignUpGenderAction(option.value))
  }

  private onSettlementChange = (settlement: string) => {
    this.props.dispatch(setSignUpSettlementAction(settlement))
  }

  private onIsPrmChange = (isPrm: boolean) => {
    this.props.dispatch(setSignUpIsPrmAction(isPrm))
  }

  private onAdditionalPhoneChange = (phone: string) => {
    this.props.dispatch(setSignUpAdditionalPhoneAction(phone))
  }

  private onAdditionalPhoneCountryChange = (country: ICountry) => {
    const { dispatch } = this.props
    dispatch(setSignUpAdditionalPhoneCountryAction(country))
    dispatch(setSignUpAdditionalPhoneCallingCodeAction(country.dial_code))
  }

  private goToStep3 = () => {
    const showDNI = process.env.REACT_APP_SHOW_DNI === 'true'
    if (!showDNI) {
      const { email } = this.props
      let { dni } = this.props
      dni = email.split('@')[0]
      this.onDNIChange(dni)
    }
    this.props.history.push(urls.signUpStep3)
  }

  private handleGoBack = (): void => {
    history.back()
  }
}

const mapStateToProps = ({ signUp }: IStoreState) => ({
  name: signUp.name,
  lastName: signUp.lastName,
  dni: signUp.dni,
  email: signUp.email,
  birthDay: signUp.birthDay,
  birthMonth: signUp.birthMonth,
  birthYear: signUp.birthYear,
  gender: signUp.gender,
  isPrm: signUp.isPrm,
  settlement: signUp.settlement,
  additionalPhone: signUp.additionalPhone,
  additionalPhoneCountry: signUp.additionalPhoneCountry,
  additionalPhoneCallingCode: signUp.additionalPhoneCallingCode
})

export default withRouter(connectRedux(mapStateToProps, SignUpStep2))
